import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import NavBar from '../components/layout/NavBar'
import { getDisputeDetail, resolveDispute } from '../api/adminDisputes'
import { useMe } from '../hooks/useMe'
import { getToken } from '../lib/auth'
import { formatPixeAtomic } from '../lib/formatPixe'

type Resolution = 'RELEASE_TO_PRO' | 'REFUND_TO_CLIENT' | 'SPLIT'

function who(user?: { handle?: string; display_name?: string } | null) {
  if (!user) return '—'
  return user.display_name || user.handle || 'User'
}

export default function AdminDisputeDetail() {
  const { jobId } = useParams()
  const authed = Boolean(getToken())
  const { me, loading: meLoading } = useMe(authed)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<any | null>(null)
  const [result, setResult] = useState<any | null>(null)
  const [resolution, setResolution] = useState<Resolution>('RELEASE_TO_PRO')
  const [splitPercent, setSplitPercent] = useState(50)
  const [adminNote, setAdminNote] = useState('')

  useEffect(() => {
    if (!meLoading && me?.is_staff && jobId) {
      setLoading(true)
      setError(null)
      getDisputeDetail(jobId)
        .then(setData)
        .catch((e: any) => setError(e?.response?.data?.detail || e?.message || 'Failed to load dispute'))
        .finally(() => setLoading(false))
    }
  }, [jobId, meLoading, me?.is_staff])

  const totalAtomic = Number(data?.suggestions?.total_pixe || 0)
  const feeBps = Number(data?.suggestions?.fee_bps || 1000)
  const splitBps = Math.round(splitPercent * 100)
  const proGross = Math.floor((totalAtomic * splitBps) / 10000)
  const fee = Math.floor((proGross * feeBps) / 10000)
  const proNet = proGross - fee
  const clientRefund = totalAtomic - proGross

  const resolveDisabled = useMemo(() => {
    if (!data?.job) return true
    if (String(data.job.status) !== 'DISPUTED') return true
    if (resolution === 'SPLIT' && (splitPercent < 0 || splitPercent > 100)) return true
    return false
  }, [data?.job, resolution, splitPercent])

  async function onResolve() {
    if (!data?.job || !jobId) return
    if (!window.confirm('This resolution is irreversible. Continue?')) return
    try {
      setSaving(true)
      setError(null)
      const payload: any = { resolution, admin_note: adminNote.trim() || undefined }
      if (resolution === 'SPLIT') payload.split_bps_to_pro = splitBps
      const res = await resolveDispute(jobId, payload)
      setResult(res)
      const fresh = await getDisputeDetail(jobId)
      setData(fresh)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Resolve failed')
    } finally {
      setSaving(false)
    }
  }

  if (!authed) return null
  if (meLoading) return <div className="p-6 text-white/70">Loading...</div>
  if (!me?.is_staff) return <div className="p-6 text-white/70">Not found</div>

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />
      <main className="mx-auto max-w-7xl px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-extrabold">Dispute Detail</h1>
          <Link to="/admin/disputes" className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">Back to list</Link>
        </div>
        {loading ? <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-white/70">Loading...</div> : null}
        {error ? <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-red-200">{error}</div> : null}
        {!loading && data?.job ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <section className="lg:col-span-2 space-y-4">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-2">
                <div className="text-sm text-white/60">Job #{data.job.job_id} • Post #{data.job.post_id}</div>
                <h2 className="text-xl font-bold">{data.job.title || `Job #${data.job.job_id}`}</h2>
                <div className="text-sm text-white/80">{data.job.summary}</div>
                <div className="text-sm text-white/70">Client: {who(data.job.client)} | Pro: {who(data.job.pro)}</div>
                <div className="text-sm text-white/70">Escrow: {formatPixeAtomic(data.job.escrow_amount_pixe)} PIXE ({data.job.escrow_status})</div>
                <div className="text-sm text-white/70">Opened: {data.job.dispute_opened_at ? new Date(data.job.dispute_opened_at).toLocaleString() : '—'}</div>
                {data.job.auto_release_at ? <div className="text-sm text-white/70">Auto release: {new Date(data.job.auto_release_at).toLocaleString()}</div> : null}
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                <h3 className="text-lg font-semibold">Evidence</h3>
                {!data.evidence?.length ? <div className="text-sm text-white/60">No evidence submitted.</div> : null}
                {data.evidence?.map((row: any) => (
                  <div key={row.id} className="rounded-lg border border-white/10 p-3">
                    <div className="text-xs text-white/60">{who(row.submitted_by)} • {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</div>
                    <div className="text-sm text-white/90 mt-1">{row.message}</div>
                    {Array.isArray(row.attachments) && row.attachments.length ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {row.attachments.map((url: string, idx: number) => (
                          <a
                            key={`${row.id}-${idx}`}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs underline text-sky-300 hover:text-sky-200"
                          >
                            Attachment {idx + 1}
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                <h3 className="text-lg font-semibold">Escrow Timeline</h3>
                {!data.escrow_events?.length ? <div className="text-sm text-white/60">No events.</div> : null}
                {data.escrow_events?.map((ev: any) => (
                  <div key={ev.id} className="rounded-lg border border-white/10 p-3 text-sm">
                    <div className="text-white/90 font-semibold">{ev.event_type}</div>
                    <div className="text-xs text-white/60">
                      {ev.created_at ? new Date(ev.created_at).toLocaleString() : '—'} • {who(ev.actor)}
                    </div>
                    {(ev.amount_pixe != null || ev.fee_pixe != null || ev.net_pixe != null) ? (
                      <div className="text-xs text-white/70 mt-1">
                        Amount {formatPixeAtomic(ev.amount_pixe)} • Fee {formatPixeAtomic(ev.fee_pixe)} • Net {formatPixeAtomic(ev.net_pixe)}
                      </div>
                    ) : null}
                    {ev.note ? <div className="text-xs text-white/70 mt-1">{ev.note}</div> : null}
                  </div>
                ))}
              </div>
            </section>

            <aside className="space-y-3">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3 lg:sticky lg:top-24">
                <h3 className="text-lg font-semibold">Resolve Dispute</h3>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={resolution === 'RELEASE_TO_PRO'} onChange={() => setResolution('RELEASE_TO_PRO')} />
                  Release to Pro
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={resolution === 'REFUND_TO_CLIENT'} onChange={() => setResolution('REFUND_TO_CLIENT')} />
                  Refund to Client
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={resolution === 'SPLIT'} onChange={() => setResolution('SPLIT')} />
                  Split
                </label>

                {resolution === 'SPLIT' ? (
                  <div className="space-y-2 rounded-lg border border-white/10 bg-white/5 p-3">
                    <label className="text-xs text-white/70">Pro percentage</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={splitPercent}
                      onChange={(e) => setSplitPercent(Math.max(0, Math.min(100, Number(e.target.value || 0))))}
                      className="w-full rounded border border-white/15 bg-white/5 px-2 py-1 text-sm"
                    />
                    <div className="text-xs text-white/70">
                      Pro gross: {formatPixeAtomic(proGross)} PIXE
                      <br />
                      Platform fee: {formatPixeAtomic(fee)} PIXE
                      <br />
                      Pro net: {formatPixeAtomic(proNet)} PIXE
                      <br />
                      Client refund: {formatPixeAtomic(clientRefund)} PIXE
                    </div>
                  </div>
                ) : null}

                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={4}
                  placeholder="Admin note (optional)"
                  className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={onResolve}
                  disabled={resolveDisabled || saving}
                  className="w-full rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  {saving ? 'Resolving...' : 'Resolve dispute'}
                </button>

                {result ? (
                  <div className="rounded-lg border border-emerald-400/25 bg-emerald-500/10 p-3 text-xs text-emerald-100">
                    <div className="font-semibold">Resolved: {result.resolution}</div>
                    <div>Total: {formatPixeAtomic(result.total_pixe)} PIXE</div>
                    <div>Fee: {formatPixeAtomic(result.fee_pixe)} PIXE</div>
                    <div>Pro net: {formatPixeAtomic(result.pro_net_pixe)} PIXE</div>
                    <div>Client refund: {formatPixeAtomic(result.client_refund_pixe)} PIXE</div>
                  </div>
                ) : null}
              </div>
            </aside>
          </div>
        ) : null}
      </main>
    </div>
  )
}
